// module/frontend/routes/index.js
import { h } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import BrainAssistantsList from 'dashboard/components/pipelines/brain/BrainAssistantsList.vue';

import PipelinesKanban from 'dashboard/components/pipelines/PipelinesKanban.vue';
import PipelineSettings from 'dashboard/components/pipelines/settings/PipelineSettings.vue';
import CalendarView from 'dashboard/components/pipelines/CalendarView.vue';
import KnowledgeBaseList from 'dashboard/components/pipelines/knowledge-base/KnowledgeBaseList.vue';
import KnowledgeBaseSettings from 'dashboard/components/pipelines/knowledge-base/KnowledgeBaseSettings.vue';
import PermissionsIndex from 'dashboard/components/pipelines/permissions/PermissionsIndex.vue';
import TrackingApiKeys from 'dashboard/components/pipelines/settings/TrackingApiKeys.vue';
import PipelinePaywall from 'dashboard/components/pipelines/PipelinePaywall.vue';
import FeatureGatedPage from 'dashboard/components/pipelines/FeatureGatedPage.vue';
import CommunityExternalRedirect from 'dashboard/components/pipelines/CommunityExternalRedirect.vue';
import SettingsWrapper from 'dashboard/routes/dashboard/settings/SettingsWrapper.vue';
import InternalChatLayout from 'dashboard/components/internalChat/InternalChatLayout.vue';
import DraftsList from 'dashboard/components/internalChat/DraftsList.vue';

// Cria um wrapper component que exibe FeatureGatedPage com o slot correto.
// Evita guards beforeEnter (que têm problemas de timing com vuex-router-sync).
const gated = (feature, component) => ({
  render: () => h(FeatureGatedPage, { feature }, { default: () => h(component) }),
});

const EmptyComponent = { template: '<div />' };

const INTERNAL_CHAT_PERMISSIONS = ['administrator', 'agent'];

export const routes = [
  // Rota de paywall — exibida quando a feature do plano está bloqueada
  {
    path: frontendURL('accounts/:accountId/plan-upgrade'),
    name: 'pipeline_paywall',
    component: PipelinePaywall,
    props: route => ({ feature: route.query.feature || '' }),
    meta: { permissions: ['administrator', 'agent'] },
  },

  // ── Pipeline ──────────────────────────────────────────────────────────────
  {
    path: frontendURL('accounts/:accountId/pipelines'),
    name: 'pipelines_list',
    component: gated('pipeline', PipelinesKanban),
    meta: { permissions: ['administrator', 'agent'] },
  },
  {
    path: frontendURL('accounts/:accountId/settings/pipelines/:pipelineId'),
    component: SettingsWrapper,
    children: [
      {
        path: '',
        name: 'pipeline_settings',
        component: gated('pipeline', PipelineSettings),
        meta: { permissions: ['administrator'] },
      },
    ],
  },

  // ── Calendário ───────────────────────────────────────────────────────────
  {
    path: frontendURL('accounts/:accountId/calendar'),
    name: 'calendar_list',
    component: gated('calendar', CalendarView),
    meta: { permissions: ['administrator', 'agent'] },
  },

  // ── Base de Conhecimento ──────────────────────────────────────────────────
  {
    path: frontendURL('accounts/:accountId/settings/knowledge-bases'),
    component: SettingsWrapper,
    children: [
      {
        path: '',
        name: 'knowledge_bases_list',
        component: gated('knowledge_base', KnowledgeBaseList),
        meta: { permissions: ['administrator'] },
      },
    ],
  },
  {
    path: frontendURL('accounts/:accountId/settings/knowledge-bases/:knowledgeBaseId'),
    component: SettingsWrapper,
    children: [
      {
        path: '',
        name: 'knowledge_base_settings',
        component: gated('knowledge_base', KnowledgeBaseSettings),
        meta: { permissions: ['administrator'] },
      },
    ],
  },

  // ── Comunidade (redirect externo) ─────────────────────────────────────────
  {
    path: frontendURL('accounts/:accountId/community-external'),
    name: 'community_external',
    component: CommunityExternalRedirect,
    meta: { permissions: ['administrator', 'agent'] },
  },

  // ── Chat Interno ─────────────────────────────────────────────────────────
  {
    path: frontendURL('accounts/:accountId/internal-chat'),
    name: 'internal_chat',
    component: InternalChatLayout,
    meta: { permissions: INTERNAL_CHAT_PERMISSIONS },
    children: [
      {
        path: '',
        name: 'internal_chat_home',
        component: EmptyComponent,
        meta: { permissions: INTERNAL_CHAT_PERMISSIONS },
      },
      {
        path: 'channels/:channelId',
        name: 'internal_chat_channel',
        component: EmptyComponent,
        meta: { permissions: INTERNAL_CHAT_PERMISSIONS },
      },
      {
        path: 'channels/:channelId/thread/:messageId',
        name: 'internal_chat_thread',
        component: EmptyComponent,
        meta: { permissions: INTERNAL_CHAT_PERMISSIONS },
      },
      {
        path: 'dm/:channelId',
        name: 'internal_chat_dm',
        component: EmptyComponent,
        meta: { permissions: INTERNAL_CHAT_PERMISSIONS },
      },
      {
        path: 'drafts',
        name: 'internal_chat_drafts',
        component: DraftsList,
        meta: { permissions: INTERNAL_CHAT_PERMISSIONS },
      },
    ],
  },

  // ── Tracking API Keys ─────────────────────────────────────────────────────
  {
    path: frontendURL('accounts/:accountId/settings/tracking-api-keys'),
    component: SettingsWrapper,
    children: [
      {
        path: '',
        name: 'tracking_api_keys_list',
        component: TrackingApiKeys,
        meta: { permissions: ['administrator'] },
      },
    ],
  },

  // ── Permissões ───────────────────────────────────────────────────────────
  {
    path: frontendURL('accounts/:accountId/settings/permissions'),
    component: SettingsWrapper,
    children: [
      {
        path: '',
        name: 'permissions_settings',
        component: gated('permissions', PermissionsIndex),
        meta: { permissions: ['administrator'] },
      },
    ],
  },

];

// MODULO BRAIN - AUTO-INJETADO
export const pipelineBrainRoutes = [
  {
    path: frontendURL('accounts/:accountId/brain'),
    name: 'pipeline_brain_root',
    redirect: { name: 'pipeline_brain_assistants' },
    meta: { permissions: ['administrator'] },
  },
  {
    path: frontendURL('accounts/:accountId/brain/assistants'),
    name: 'pipeline_brain_assistants',
    component: BrainAssistantsList,
    meta: { permissions: ['administrator'] },
  },
  {
    path: frontendURL('accounts/:accountId/brain/settings'),
    name: 'pipeline_brain_settings',
    component: BrainAssistantsList,
    meta: { permissions: ['administrator'] },
  },
  {
    // Rota "Sincronizar" — usa o mesmo componente da lista, que dispara
    // brain/syncAgents quando detecta query ?sync=1 e redireciona de volta.
    path: frontendURL('accounts/:accountId/brain/sync'),
    name: 'pipeline_brain_sync',
    component: BrainAssistantsList,
    props: { autoSync: true },
    meta: { permissions: ['administrator'] },
  },
];

export default routes;
